import type { ColorSystemOptions } from "@mui/material/styles";

import { chartSeriesVar } from "../chartSeries";

export const lightUiTokens: ColorSystemOptions["ui"] = {
  accent: {
    // See the note in `darkUi`: this dresses chrome, not charts.
    data: "var(--mui-palette-primary-main)"
  },
  button: {
    primary: "var(--mui-palette-primary-main)",
    primaryDeep: "var(--mui-palette-primary-main)"
  },
  card: {
    background: "rgba(255, 255, 255, 0.6)",
    border: "#E1E1E1",
    highlight: "rgba(255, 255, 255, 0.25)",
    shadow: "rgba(10, 13, 18, 0.12)"
  },
  dashboardCard: {
    background: "rgba(255, 255, 255, 0.58)",
    border: {
      from: "rgba(15, 23, 42, 0.08)",
      to: "rgba(15, 23, 42, 0.04)"
    },
    bottomOverlay: "rgb(48 67 112 / 0.02)",
    hoverBackground: "rgba(255, 255, 255, 1)",
    topOverlay: "rgba(255, 255, 255, 0.86)"
  },
  drawer: {
    background: "rgb(255 255 255 / 0.95)",
    border: {
      from: "rgba(255, 255, 255, 0.96)",
      mid: "rgba(255, 255, 255, 0.56)",
      to: "rgba(15, 23, 42, 0.09)"
    },
    bottomShade: "rgba(15, 23, 42, 0.01)",
    fallbackBackground: "#ECEEF3",
    shadow: "0 24px 60px rgba(10, 13, 18, 0.16)",
    topHighlight: "rgba(255, 255, 255, 0.70)"
  },
  glassInteractive: {
    default: {
      background: "rgba(243, 244, 246, 0.92)",
      border: {
        from: "rgba(255, 255, 255, 0.94)",
        mid: "rgba(255, 255, 255, 0.52)",
        to: "rgba(224, 224, 226, 0.24)"
      },
      bottomOverlay: "rgba(220, 220, 222, 0.18)",
      shadow:
        "0 6px 16px rgba(10, 13, 18, 0.08), 0 1px 4px rgba(10, 13, 18, 0.04)",
      topOverlay: "rgba(255, 255, 255, 0.82)"
    },
    emphasized: {
      background: "rgba(248, 249, 251, 0.96)",
      border: {
        from: "rgba(255, 255, 255, 1)",
        mid: "rgba(255, 255, 255, 0.72)",
        to: "rgba(216, 216, 218, 0.38)"
      },
      bottomOverlay: "rgba(214, 214, 216, 0.26)",
      shadow:
        "0 4px 16px rgba(10, 13, 18, 0.09), 0 1px 6px rgba(10, 13, 18, 0.05)",
      topOverlay: "rgba(255, 255, 255, 0.96)"
    }
  },
  // A near-white ground carrying a wash of the accent, per the design — the
  // deep green band belongs to dark mode.
  // Mixed over white from the chart ramp's slot 1 — on Ocean that gives
  // #EBF5F9 for the tint and #A2CEE0 for the peak, the design's own values to
  // within a few points, and it gives Desert a sand and Luna a lavender without
  // anyone having to pick them.
  hero: {
    background: {
      base: `color-mix(in srgb, ${chartSeriesVar(0)} 2%, #FFFFFF)`,
      // Mixing a mid tone to white greys it as well as lightens it, so the
      // peak reads heavier than its lightness suggests. Lighter is the correct
      // side to miss on - this sits under a full row of black ink.
      peak: `color-mix(in srgb, ${chartSeriesVar(0)} 19.5%, #FFFFFF)`,
      tint: `color-mix(in srgb, ${chartSeriesVar(0)} 7%, #FFFFFF)`
    },
    border: "var(--mui-palette-primary-100)",
    pill: "rgba(255, 255, 255, 0.8)",
    text: "var(--mui-palette-text-primary)"
  },
  logo: {
    gradient: {
      from: "var(--mui-palette-primary-main)",
      to: "var(--mui-palette-primary-dark)"
    },
    mark: "var(--mui-palette-primary-main)"
  },
  map: {
    land: "#B9BFDC",
    rippleInner: "rgba(255, 255, 255, 0.3)",
    rippleOutter: "rgba(255, 255, 255, 1)"
  },
  // Derived from the selected primary palette rather than fixed per accent:
  // the nav ground is a 5% wash of the accent over white, hover 10%, the
  // open-group block 7.5%, and the rows' selected tint is the palette's own
  // 50 step with a 13% ring. That is what lets Forest, Ocean, Desert, Luna and
  // Night share one definition.
  sidebar: {
    caption: "rgba(var(--mui-palette-text-primaryChannel) / 0.5)",
    card: {
      background: "#FFFFFF",
      hover: "#F7F6F3"
    },
    item: {
      icon: "rgba(var(--mui-palette-text-primaryChannel) / 0.5)",
      // See the note in `darkUi`: the same value `logo.mark` resolves to.
      selected: {
        background: "var(--mui-palette-primary-50)",
        ring: "rgba(var(--mui-palette-primary-mainChannel) / 0.13)",
        text: "var(--mui-palette-primary-main)"
      },
      text: "rgba(var(--mui-palette-text-primaryChannel) / 0.82)"
    },
    line: "#D9D9D9",
    nav: {
      background:
        "color-mix(in srgb, var(--mui-palette-primary-main) 5%, #FFFFFF)",
      group:
        "color-mix(in srgb, var(--mui-palette-primary-main) 7.5%, #FFFFFF)",
      hover: "color-mix(in srgb, var(--mui-palette-primary-main) 10%, #FFFFFF)",
      line: "color-mix(in srgb, var(--mui-palette-primary-main) 14%, #FFFFFF)"
    }
  },
  table: {
    bodyRowHover: "#F5F6F7",
    bodyRowShadow: "#F5F6F7",
    border: "#E9EAEB",
    cellError: "rgb(246, 219, 223)",
    cellGreen: "rgb(171, 218, 176)",
    cellRed: "rgb(218, 171, 179)",
    headerRowShadow: "#F7F7FC"
  },
  topBar: {
    background: "var(--mui-palette-background-default)",
    crumbScope: "rgba(var(--mui-palette-text-primaryChannel) / 0.55)",
    crumbSection: "var(--mui-palette-text-primary)",
    hover: "rgba(var(--mui-palette-text-primaryChannel) / 0.05)",
    line: "#D9D9D9",
    lineHover: "rgba(var(--mui-palette-text-primaryChannel) / 0.28)",
    separator: "rgba(var(--mui-palette-text-primaryChannel) / 0.35)"
  }
};
